import React from 'react';
import { ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
}

/** A labelled group of options rendered as a native <optgroup>. */
export interface SelectOptionGroup {
  label: string;
  options: SelectOption[];
}

export interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  /** Flat option list. Mutually exclusive with `groups`. */
  options?: SelectOption[];
  /** Grouped option list rendered as <optgroup> sections. Mutually exclusive with `options`. */
  groups?: SelectOptionGroup[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  options,
  groups,
  placeholder = 'Select an option',
  disabled = false,
  className = '',
}) => {
  return (
    <div className={`relative ${className}`}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="
          w-full appearance-none
          bg-bg-input text-text-primary
          border border-border-input rounded-md
          px-4 py-2 pr-10
          focus:outline-none focus:ring-2 focus:ring-accent-red focus:border-accent-red
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors duration-200
          cursor-pointer
        "
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {groups
          ? groups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))
          : (options ?? []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <ChevronDown className="w-4 h-4 text-text-secondary" />
      </div>
    </div>
  );
};
